const numericId = (value) => Number(value);

export const getAttemptNumber = (attempt, attempts = []) => {
    if (!attempt) return '—';
    if (Number.isFinite(Number(attempt.attempt_number))) return Number(attempt.attempt_number);

    const examId = numericId(attempt.exam ?? attempt.exam_id);
    const ordered = attempts
        .filter((item) => numericId(item.exam ?? item.exam_id) === examId)
        .sort((left, right) => {
            const leftTime = new Date(left.started_at || left.submitted_at || 0).getTime();
            const rightTime = new Date(right.started_at || right.submitted_at || 0).getTime();
            return leftTime - rightTime || numericId(left.id) - numericId(right.id);
        });
    const index = ordered.findIndex((item) => numericId(item.id) === numericId(attempt.id));
    return index >= 0 ? index + 1 : attempt.id;
};

export const getStudentExamAvailability = (exam, timing, results = []) => {
    const completedAttempts = results.filter((item) => numericId(item.exam ?? item.exam_id) === numericId(exam.id)).length;
    const maxAttempts = Number(exam.effective_max_attempts ?? exam.max_attempts ?? 1);
    const canResume = exam.latest_attempt_status === 'in_progress';
    const attemptsExhausted = timing === 'open' && !canResume && completedAttempts >= maxAttempts;

    return {
        canStart: timing === 'open' && !attemptsExhausted,
        canResume,
        attemptsExhausted,
        completedAttempts,
        maxAttempts,
    };
};

export const orderStudentResultAnswers = (answers = []) => [...answers].sort((left, right) => {
    const leftPosition = Number(left.position);
    const rightPosition = Number(right.position);
    const safeLeftPosition = Number.isFinite(leftPosition) ? leftPosition : Number.MAX_SAFE_INTEGER;
    const safeRightPosition = Number.isFinite(rightPosition) ? rightPosition : Number.MAX_SAFE_INTEGER;
    return safeLeftPosition - safeRightPosition || numericId(left.id) - numericId(right.id);
});

export const getResultGradingState = (result) => {
    if (result?.score === null || result?.score === undefined) {
        return {complete: false, label: 'منتظر انتشار', pendingCount: 0};
    }
    const pendingCount = (result.answers || [])
        .filter((answer) => answer.grading_status === 'not_graded').length;
    return {
        complete: pendingCount === 0,
        label: pendingCount ? `${pendingCount} پاسخ در انتظار تصحیح` : 'تصحیح کامل',
        pendingCount,
    };
};

const resultExamKey = (result) => String(result?.exam ?? result?.exam_id ?? result?.exam_title ?? result?.id);
const resultTime = (result) => new Date(result?.updated_at || result?.submitted_at || result?.started_at || 0).getTime();

export const groupStudentResults = (results = []) => {
    const groups = new Map();
    results.forEach((result) => {
        const key = resultExamKey(result);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(result);
    });

    return Array.from(groups.entries()).map(([key, attempts]) => {
        const orderedAttempts = [...attempts].sort((left, right) => resultTime(right) - resultTime(left));
        const releasedAttempts = orderedAttempts.filter((attempt) => attempt.score !== null && attempt.score !== undefined);
        const bestAttempt = releasedAttempts.reduce((best, attempt) => {
            if (!best || Number(attempt.score) > Number(best.score)) return attempt;
            return best;
        }, null);
        return {
            key,
            examTitle: orderedAttempts[0]?.exam_title || `آزمون ${key}`,
            attempts: orderedAttempts,
            latestAttempt: orderedAttempts[0],
            bestAttempt,
            hasReleasedResult: releasedAttempts.length > 0,
        };
    }).sort((left, right) => resultTime(right.latestAttempt) - resultTime(left.latestAttempt));
};

export const filterStudentResultGroups = (groups, filter = 'all', search = '') => {
    const term = search.trim().toLocaleLowerCase('fa-IR');
    return groups.filter((group) => {
        if (term && !group.examTitle.toLocaleLowerCase('fa-IR').includes(term)) return false;
        if (filter === 'released') return group.hasReleasedResult;
        if (filter === 'pending') return !group.hasReleasedResult || group.attempts.some((attempt) => attempt.score == null);
        return true;
    });
};
